import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import SecureSessionService from '../session/SecureSessionService';

/**
 * Axios client configured with interceptors for session management
 * - SessionId is sent in request body/params
 * - Handles 401 unauthorized responses by dispatching session-expired event
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    const expiresInMinutes = response.headers['x-session-expires-in-minutes'];
    
    if (expiresInMinutes) {
      if (expiresInMinutes === '0') {
        window.dispatchEvent(new CustomEvent('session-expired'));
        return response;
      }
      
      const minutes = parseInt(expiresInMinutes, 10);
      const expiresAt = Date.now() + (minutes * 60 * 1000);
    
      const session = SecureSessionService.getRawSession();
      if (session && !isNaN(minutes) && expiresAt > 0) {
        SecureSessionService.storeSession({
          sessionId: session.sessionId,
          userId: session.userId,
          expiresAt: expiresAt
        });
      
        window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt } }));
      }
    }
    
    return response;
  },
  (error) => {
     if (error.response) {
      const expiresInMinutes = error.response.headers?.['x-session-expires-in-minutes'];
      const session = SecureSessionService.getRawSession();

      if (session) {
        if (expiresInMinutes && expiresInMinutes !== '0') {
          const minutes = parseInt(expiresInMinutes, 10);
          const expiresAt = Date.now() + (minutes * 60 * 1000);
          if (!isNaN(minutes) && expiresAt > 0) {
            SecureSessionService.storeSession({ sessionId: session.sessionId, userId: session.userId, expiresAt });
            window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt } }));
          } else {
            window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: session.expiresAt } }));
          }
        } else {
            window.dispatchEvent(new CustomEvent('session-refreshed', { detail: { expiresAt: session.expiresAt } }));
        }
      }
    }

    if (error.response?.status === 401) {
      const message = error.response?.data?.message || '';
      
      const isSessionExpired = message.includes('Session not found or expired') ||
                               message.includes('Session expired') ||
                               message.includes('Session invalid');
      
      if (isSessionExpired) {
        window.dispatchEvent(new CustomEvent('session-expired'));
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
